/**
 * The four aggregate report figures: how they are mapped from the model, and
 * how they are turned into presentational rows.
 *
 * Ported from the design source `compass-oppreport.jsx` (the `FIGURES(yrs)`
 * constant and the `figures` section) in Claude Design project
 * `79357508-fe09-4122-831f-a0014c6a31b0`. Fixed by
 * `docs/frontend/intake-report-port-contract.md` §2 "Phase 2".
 *
 * Two rules govern this module:
 *
 * 1. **Map, never compute.** `buildReportFigures` reads the same
 *    `InvestmentCurveData` the investment timeline renders. Unit conversion
 *    (months → years) and formatting are the only transformations. Nothing
 *    here re-derives a number that `@atlas/computation` already produces —
 *    if Figures and the existing calculation path can disagree, they will.
 * 2. **A fallback horizon is flagged `assumed`.** Only category-derived
 *    models emit a `contract_term_years` assumption; every other model runs on
 *    the 36-month default. Plan §6 risk 1: shipping "Net 3-year savings" on a
 *    default presents an assumption as a finding, so every horizon-bearing row
 *    sets `horizonAssumed` and its description says so in full. An explicit
 *    contract term renders unqualified.
 *
 *    The qualifier is a flag rather than an " (assumed)" suffix on `label`
 *    because tile labels are one line and ellipsed — the suffix was being
 *    truncated away, hiding the one thing it exists to say. Consumers render
 *    it as a badge.
 */

import type { InvestmentCurveData } from "@atlas/computation";
import {
  formatHorizonYears,
  formatReportRoiPercent,
  formatReportUsd,
  REPORT_EMPTY_VALUE,
} from "./report-format";
import type { ReportFigureRow, ReportFigures } from "./report-types";

/* ------------------------------------------------------------------ *
 * Presentational rows
 * ------------------------------------------------------------------ */

/** Appended to the description of every row that genuinely spans the horizon. */
function assumedHorizonNote(horizonLabel: string): string {
  return ` The ${horizonLabel} horizon is assumed, not an explicit contract term.`;
}

/**
 * Design `FIGURES(yrs)` — the four rows in document order. The horizon is
 * interpolated into the two labels that span it (`netSavings`, `totalCost`)
 * and, when it is a fallback, into their descriptions. `roi` deliberately
 * names no horizon; see the comment on that row.
 *
 * `value` is formatted here rather than in the component so the row and the
 * tile of the same figure can never drift. `available` holds the invariant
 * `available === (value !== "—")`, which is what a consumer needs to decide
 * whether a drill-in is worth offering.
 */
export function buildReportFigureRows(
  figures: ReportFigures,
): ReportFigureRow[] {
  const { ready, horizonYears, horizonAssumed } = figures;
  const horizonLabel = formatHorizonYears(horizonYears);
  // A horizon we do not have cannot be assumed — the qualifier only applies
  // where the copy actually names a number.
  const assumed = horizonLabel != null && horizonAssumed;
  const descriptionNote =
    assumed && horizonLabel ? assumedHorizonNote(horizonLabel) : "";

  return [
    {
      key: "roi",
      // Deliberate divergence from the design's copy, which reads "Return over
      // N years against total cost of ownership." The design's store computes
      // ROI over the horizon; ours does not. `summary.roiPercent` is
      // `(annual benefit − annual cost) ÷ annual cost`
      // (`packages/computation/src/custom-model/compute.ts:135`) — a
      // steady-state annual ratio that ignores both the rollout ramp and any
      // one-time cost. Describing it as spanning the horizon would ship a
      // false statement next to a true number, so this row names no horizon
      // and takes no assumed-horizon note. Plan §8 follow-up #5 tracks the
      // fix: a curve-derived ROI upstream, over the same window as
      // `netSavings` and `totalCost`. Restore the design's sentence only once
      // that lands.
      label: "Return on investment",
      description: "Annual net benefit against annual cost, at full run-rate.",
      // Rounded at the display edge, as the design does — see
      // `formatReportRoiPercent`, which the executive summary's `roi`
      // reference shares so the tile and the prose cannot disagree.
      value:
        ready && figures.roiPercent != null
          ? formatReportRoiPercent(figures.roiPercent)
          : REPORT_EMPTY_VALUE,
      available: ready && figures.roiPercent != null,
      // This row names no horizon, so it can never be qualified by one.
      horizonAssumed: false,
    },
    {
      key: "netSavings",
      label: horizonYears ? `Net ${horizonYears}-year savings` : "Net savings",
      description: `Total rollout-adjusted benefit minus all program costs.${descriptionNote}`,
      value: ready ? formatReportUsd(figures.netSavings) : REPORT_EMPTY_VALUE,
      available: ready && figures.netSavings != null,
      horizonAssumed: assumed,
    },
    {
      key: "payback",
      label: "Payback period",
      description: "Time until cumulative benefit covers cumulative cost.",
      value:
        ready && figures.paybackLabel != null
          ? figures.paybackLabel
          : REPORT_EMPTY_VALUE,
      available: ready && figures.paybackLabel != null,
      // "Beyond 3 years" interpolates the horizon into the value, so a
      // fallback horizon qualifies this row even though its label does not
      // name one.
      horizonAssumed: assumed,
    },
    {
      key: "totalCost",
      label: horizonLabel ? `Total cost over ${horizonLabel}` : "Total cost",
      description: `Subscription and implementation across the evaluation window.${descriptionNote}`,
      value: ready ? formatReportUsd(figures.totalCost) : REPORT_EMPTY_VALUE,
      available: ready && figures.totalCost != null,
      horizonAssumed: assumed,
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Mapping the model onto `ReportFigures`
 * ------------------------------------------------------------------ */

/**
 * Everything `buildReportFigures` needs, with each field's origin named.
 *
 * The curve fields and the read-model fields come from two different places on
 * purpose: the curve is the only thing that knows about the horizon, and the
 * read model is the only thing that knows whether the projection is computed.
 */
export interface BuildReportFiguresInput {
  /**
   * `AssumptionsView.summary.projectionStatus`
   * (`apps/api/src/compass/services/roi-assumption-service.ts`), reaching the
   * web app through the assumptions read-model query. The single gate: false
   * ⇒ every figure renders `—`.
   */
  projectionStatus: "uncomputed" | "computed";
  /** `AssumptionsView.summary.roiPercent` — the persisted model's ROI. */
  roiPercent: number | null;
  /**
   * `toInvestmentTimelineData(...)?.curveData`
   * (`apps/web/lib/compass/investment-timeline.ts`) — the same
   * `InvestmentCurveData` the investment timeline renders. `null` whenever
   * that helper returns `null` (uncomputed model, no value-stream breakdown,
   * or no classified families), in which case the three curve-backed figures
   * stay `null`.
   */
  curve: InvestmentCurveData | null;
  /**
   * `toInvestmentTimelineData(...)?.contractTermMonths` — the term the curve
   * was actually generated over, already floored at 12 months. This, not the
   * assumption, is the source of the horizon, so a figure can never be
   * labelled with a window the curve did not use.
   */
  contractTermMonths: number | null;
  /**
   * The model's explicit `contract_term_years` assumption, or `null` when it
   * has none and the 36-month default applied. Only its *presence* is read —
   * the horizon itself always comes from `contractTermMonths`.
   */
  explicitContractTermYears: number | null;
}

/** Months → years, kept to one decimal so 18 months reads "1.5", not "1.5000000000000002". */
function monthsToYears(months: number): number {
  return Math.round((months / 12) * 10) / 10;
}

/**
 * A payback that lands inside the first year, in the unit a reader thinks in.
 *
 * The design's `F.payback` is always a year figure, which is fine at "1.1
 * years" and useless below one: a real model paid back in 2.4 months rendered
 * "0.2 years", a number nobody converts in their head. Weeks under a quarter,
 * whole months up to a year, years above it — one label, so the figure tile and
 * the executive summary cannot state the same payback two ways.
 */
function formatPaybackMonths(months: number): string | null {
  if (months < 3) {
    // 4.345 weeks to the month, so 2.4 months reads "about 10 weeks".
    const weeks = Math.max(1, Math.round(months * 4.345));
    return `about ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  }
  if (months < 12) {
    const whole = Math.round(months);
    return `about ${whole} ${whole === 1 ? "month" : "months"}`;
  }
  return formatHorizonYears(monthsToYears(months));
}

/**
 * Design `F.payback` is already a string — `"1.1 years"` or `"Beyond N years"`
 * (plan §9 #7). `paybackMonth` is a fractional month index, so this is a unit
 * conversion plus the design's own two-branch copy, not a calculation.
 *
 * `paybackMonth === null` means `computePayback` found no month where
 * cumulative net turns positive *and* the terminal monthly net is not
 * positive either — the program does not pay back. Rendering that as `—`
 * would read as "not computed", so it takes the design's "Beyond" wording
 * whenever the horizon is known.
 */
function buildPaybackLabel(
  curve: InvestmentCurveData,
  horizonLabel: string | null,
): string | null {
  if (curve.paybackMonth == null) {
    return horizonLabel ? `Beyond ${horizonLabel}` : null;
  }
  return formatPaybackMonths(curve.paybackMonth);
}

/**
 * Maps the investment curve and the assumptions read-model summary onto
 * `ReportFigures`. Every field is a direct read of something already
 * computed upstream:
 *
 * | `ReportFigures` | Source |
 * |---|---|
 * | `ready` | `projectionStatus === "computed"` |
 * | `horizonYears` | `contractTermMonths / 12` |
 * | `horizonAssumed` | no explicit `contract_term_years` assumption |
 * | `roiPercent` | `summary.roiPercent` |
 * | `netSavings` | `curve.summary.contract.net` |
 * | `paybackLabel` | `curve.paybackMonth` |
 * | `totalCost` | `curve.summary.contract.totalCost` |
 */
export function buildReportFigures(
  input: BuildReportFiguresInput,
): ReportFigures {
  const {
    projectionStatus,
    roiPercent,
    curve,
    contractTermMonths,
    explicitContractTermYears,
  } = input;

  const horizonYears =
    contractTermMonths == null ? null : monthsToYears(contractTermMonths);

  return {
    ready: projectionStatus === "computed",
    horizonYears,
    horizonAssumed: horizonYears != null && explicitContractTermYears == null,
    roiPercent,
    netSavings: curve ? curve.summary.contract.net : null,
    paybackLabel: curve
      ? buildPaybackLabel(curve, formatHorizonYears(horizonYears))
      : null,
    totalCost: curve ? curve.summary.contract.totalCost : null,
  };
}
